import { z } from 'zod';
import { idSchema, isoDateSchema, isoDateTimeSchema, paginationQuerySchema } from './primitives';
import { roleSchema } from './rbac';
/**
 * Journalisation, notifications et paramètres (cahier des charges §13, §14, §16).
 */
/* ------------------------------------------------------------------------ */
/* Journal d'audit (§13)                                                     */
/* ------------------------------------------------------------------------ */
export const AUDIT_ACTIONS = {
    AUTH_LOGIN: 'auth.login',
    AUTH_LOGIN_FAILED: 'auth.login_failed',
    AUTH_LOGOUT: 'auth.logout',
    AUTH_PASSWORD_CHANGED: 'auth.password_changed',
    AUTH_LOCKED: 'auth.locked',
    USER_CREATED: 'user.created',
    USER_UPDATED: 'user.updated',
    USER_PASSWORD_RESET: 'user.password_reset',
    BRANCH_CREATED: 'branch.created',
    BRANCH_UPDATED: 'branch.updated',
    LOT_CREATED: 'lot.created',
    LOT_STATUS_CHANGED: 'lot.status_changed',
    LOT_FILES_INGESTED: 'lot.files_ingested',
    LOT_OCR_STARTED: 'lot.ocr_started',
    LOT_ARCHIVED: 'lot.archived',
    LOT_DELETED: 'lot.deleted',
    RECEIPT_CORRECTED: 'receipt.corrected',
    RECEIPT_VALIDATED: 'receipt.validated',
    RECEIPT_ARCHIVED: 'receipt.archived',
    RECEIPT_VIEWED: 'receipt.viewed',
    RECEIPT_DOWNLOADED: 'receipt.downloaded',
    RECEIPT_PRINTED: 'receipt.printed',
    RECEIPT_DELETED: 'receipt.deleted',
    DUPLICATE_RESOLVED: 'duplicate.resolved',
    SEARCH_PERFORMED: 'search.performed',
    SETTINGS_UPDATED: 'settings.updated',
    OCR_TEMPLATE_UPDATED: 'ocr_template.updated',
};
export const AUDIT_ACTION_LABELS = {
    'auth.login': 'Connexion',
    'auth.login_failed': 'Échec de connexion',
    'auth.logout': 'Déconnexion',
    'auth.password_changed': 'Changement de mot de passe',
    'auth.locked': 'Compte verrouillé',
    'user.created': 'Création utilisateur',
    'user.updated': 'Modification utilisateur',
    'user.password_reset': 'Réinitialisation de mot de passe',
    'branch.created': 'Création bureau',
    'branch.updated': 'Modification bureau',
    'lot.created': 'Création de lot',
    'lot.status_changed': 'Changement de statut du lot',
    'lot.files_ingested': 'Dépôt de fichiers',
    'lot.ocr_started': 'Lancement OCR',
    'lot.archived': 'Archivage du lot',
    'lot.deleted': 'Suppression de lot',
    'receipt.corrected': 'Correction de métadonnées',
    'receipt.validated': 'Validation de reçu',
    'receipt.archived': 'Archivage de reçu',
    'receipt.viewed': 'Consultation',
    'receipt.downloaded': 'Téléchargement',
    'receipt.printed': 'Impression',
    'receipt.deleted': 'Suppression de reçu',
    'duplicate.resolved': 'Résolution de doublon',
    'search.performed': 'Recherche',
    'settings.updated': 'Modification des paramètres',
    'ocr_template.updated': 'Modification des zones OCR',
};
export const auditOutcomeSchema = z.enum(['SUCCESS', 'FAILURE']);
export const auditLogSchema = z.object({
    id: idSchema,
    occurredAt: isoDateTimeSchema,
    action: z.string(),
    actionLabel: z.string(),
    outcome: auditOutcomeSchema,
    actorUserId: idSchema.nullable(),
    actorName: z.string().nullable(),
    actorEmail: z.string().nullable(),
    actorRole: roleSchema.nullable(),
    branchId: idSchema.nullable(),
    branchCode: z.string().nullable(),
    entityType: z.string().nullable(),
    entityId: z.string().nullable(),
    ipAddress: z.string().nullable(),
    userAgent: z.string().nullable(),
    correlationId: z.string().nullable(),
    metadata: z.record(z.string(), z.unknown()).nullable(),
});
export const listAuditLogsQuerySchema = paginationQuerySchema.extend({
    action: z.string().max(64).optional(),
    actorUserId: idSchema.optional(),
    branchId: idSchema.optional(),
    entityType: z.string().max(48).optional(),
    entityId: z.string().max(64).optional(),
    outcome: auditOutcomeSchema.optional(),
    dateFrom: isoDateSchema.optional(),
    dateTo: isoDateSchema.optional(),
    search: z.string().trim().max(160).optional(),
});
/* ------------------------------------------------------------------------ */
/* Notifications (§14)                                                       */
/* ------------------------------------------------------------------------ */
export const NOTIFICATION_TYPES = {
    OCR_FAILED: 'OCR_FAILED',
    DUPLICATE_DETECTED: 'DUPLICATE_DETECTED',
    LOT_COMPLETED: 'LOT_COMPLETED',
    SCANNER_ERROR: 'SCANNER_ERROR',
    LOW_DISK_SPACE: 'LOW_DISK_SPACE',
    BACKUP_FAILED: 'BACKUP_FAILED',
    REVIEW_REQUIRED: 'REVIEW_REQUIRED',
};
export const notificationTypeSchema = z.enum([
    NOTIFICATION_TYPES.OCR_FAILED,
    NOTIFICATION_TYPES.DUPLICATE_DETECTED,
    NOTIFICATION_TYPES.LOT_COMPLETED,
    NOTIFICATION_TYPES.SCANNER_ERROR,
    NOTIFICATION_TYPES.LOW_DISK_SPACE,
    NOTIFICATION_TYPES.BACKUP_FAILED,
    NOTIFICATION_TYPES.REVIEW_REQUIRED,
]);
export const NOTIFICATION_TYPE_LABELS = {
    OCR_FAILED: 'OCR échoué',
    DUPLICATE_DETECTED: 'Doublon détecté',
    LOT_COMPLETED: 'Lot terminé',
    SCANNER_ERROR: 'Erreur scanner',
    LOW_DISK_SPACE: 'Espace disque faible',
    BACKUP_FAILED: 'Sauvegarde échouée',
    REVIEW_REQUIRED: 'Validation requise',
};
export const notificationSeveritySchema = z.enum(['INFO', 'WARNING', 'CRITICAL']);
export const notificationSchema = z.object({
    id: idSchema,
    type: notificationTypeSchema,
    severity: notificationSeveritySchema,
    title: z.string(),
    body: z.string(),
    branchId: idSchema.nullable(),
    entityType: z.string().nullable(),
    entityId: z.string().nullable(),
    /** Lien applicatif à ouvrir depuis le centre de notifications. */
    actionPath: z.string().nullable(),
    readAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
export const listNotificationsQuerySchema = paginationQuerySchema.extend({
    unreadOnly: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((value) => (typeof value === 'boolean' ? value : value === 'true'))
        .optional(),
    type: notificationTypeSchema.optional(),
});
/* ------------------------------------------------------------------------ */
/* Paramètres (§15, §16, §20.2)                                              */
/* ------------------------------------------------------------------------ */
export const settingsSchema = z.object({
    scanning: z.object({
        /** Résolution minimale acceptée à l'ingestion. */
        minimumDpi: z.number().int().min(150).max(1200).default(300),
        autoDeskew: z.boolean().default(true),
        autoContrast: z.boolean().default(true),
        denoise: z.boolean().default(true),
        /**
         * Recadre chaque page sur la bande imprimée du reçu.
         * Indispensable quand une bande étroite est numérisée sur une feuille A4 :
         * les zones OCR deviennent relatives au reçu et non au papier.
         */
        cropToContent: z.boolean().default(true),
        removeBlankPages: z.boolean().default(true),
        /** Au-delà de ce ratio de pixels clairs, la page est jugée blanche. */
        blankPageThreshold: z.number().min(0.9).max(1).default(0.995),
        /** Découpage automatique : nombre de pages composant un reçu. */
        pagesPerReceipt: z.number().int().min(1).max(20).default(1),
        splitOnBlankPage: z.boolean().default(true),
    }),
    ocr: z.object({
        enabled: z.boolean().default(true),
        engine: z.enum(['TESSERACT']).default('TESSERACT'),
        languages: z.array(z.string()).default(['fra', 'eng']),
        /**
         * Confiance exigée pour qu'un reçu soit accepté sans regard humain.
         *
         * À 1, plus aucun document n'est archivé automatiquement : la
         * reconnaissance ne fait plus que **proposer** des valeurs, et chaque reçu
         * passe par la file de validation. C'est le réglage le plus strict, celui
         * qui garantit qu'aucun numéro erroné n'entre dans l'archive sans avoir été
         * vu. Le contrôle se fait alors lot par lot, d'un seul geste.
         */
        autoAcceptConfidence: z.number().min(0).max(1).default(1),
        maxAttempts: z.number().int().min(1).max(5).default(2),
        concurrency: z.number().int().min(1).max(16).default(2),
    }),
    duplicates: z.object({
        checkFileHash: z.boolean().default(true),
        checkBusinessKey: z.boolean().default(true),
        checkPerceptualHash: z.boolean().default(true),
        /**
         * Distance de Hamming maximale entre deux empreintes perceptuelles.
         * L'empreinte fait 256 bits ; 18 correspond à ~7 % de bits divergents,
         * ce qui couvre une renumérisation du même papier sans confondre deux
         * documents distincts partageant la même mise en page.
         */
        perceptualDistanceThreshold: z.number().int().min(0).max(128).default(18),
        blockArchiveOnDuplicate: z.boolean().default(true),
    }),
    ai: z.object({
        classificationEnabled: z.boolean().default(false),
        autoFillMetadata: z.boolean().default(false),
        minimumConfidence: z.number().min(0).max(1).default(0.9),
        learnFromCorrections: z.boolean().default(true),
    }),
    storage: z.object({
        /** Seuil de déclenchement de la notification « espace disque faible ». */
        lowDiskWarningGb: z.number().min(1).max(10_000).default(50),
        retentionYears: z.number().int().min(1).max(50).default(10),
    }),
    /**
     * Sauvegardes automatiques (§16, politique 3-2-1).
     *
     * Deux natures de données à protéger, et elles ne se sauvegardent pas de la
     * même façon : la base porte les métadonnées, les droits et le journal
     * d'audit — elle se vide dans un fichier ; le coffre documentaire porte les
     * PDF — il se recopie. La sauvegarde de la base est donc systématique, celle
     * des documents optionnelle, car une installation sérieuse la confie plutôt à
     * une réplication au niveau du système de fichiers.
     */
    backup: z.object({
        enabled: z.boolean().default(true),
        /** Heure de déclenchement, en heure locale du serveur (0–23). */
        hour: z.number().int().min(0).max(23).default(2),
        /** Nombre de jours de sauvegardes conservées sur place. */
        retentionDays: z.number().int().min(1).max(3650).default(30),
        /** Destination des archives. Un volume distinct est vivement conseillé. */
        destination: z.string().default('./backups'),
        /** Recopie incrémentale des documents archivés depuis la dernière sauvegarde. */
        includeDocuments: z.boolean().default(true),
    }),
    security: z.object({
        sessionMinutes: z.number().int().min(5).max(1440).default(60),
        maxFailedLogins: z.number().int().min(3).max(20).default(5),
        lockoutMinutes: z.number().int().min(1).max(1440).default(15),
        passwordExpiryDays: z.number().int().min(0).max(3650).default(0),
    }),
    ingestion: z.object({
        watchFolderEnabled: z.boolean().default(false),
        watchFolderPath: z.string().default(''),
        /** Délai de stabilité avant prise en compte d'un fichier déposé. */
        stabilityMs: z.number().int().min(250).max(60_000).default(2_000),
    }),
});
export const updateSettingsSchema = settingsSchema.partial();
export const DEFAULT_SETTINGS = settingsSchema.parse({
    scanning: {},
    ocr: {},
    duplicates: {},
    ai: {},
    storage: {},
    security: {},
    ingestion: {},
    backup: {},
});
/* ------------------------------------------------------------------------ */
/* Sauvegardes                                                               */
/* ------------------------------------------------------------------------ */
export const backupStatusSchema = z.enum(['RUNNING', 'SUCCESS', 'FAILED']);
export const BACKUP_STATUS_LABELS = {
    RUNNING: 'En cours',
    SUCCESS: 'Réussie',
    FAILED: 'Échouée',
};
export const backupTriggerSchema = z.enum(['SCHEDULED', 'MANUAL']);
export const backupEntrySchema = z.object({
    id: idSchema,
    startedAt: isoDateTimeSchema,
    finishedAt: isoDateTimeSchema.nullable(),
    status: backupStatusSchema,
    trigger: backupTriggerSchema,
    /** Nom du répertoire de la sauvegarde. Le chemin complet n'est pas exposé. */
    label: z.string(),
    databaseBytes: z.number().int().nonnegative(),
    documentBytes: z.number().int().nonnegative(),
    documentCount: z.number().int().nonnegative(),
    durationMs: z.number().int().nonnegative(),
    errorMessage: z.string().nullable(),
    triggeredByName: z.string().nullable(),
});
//# sourceMappingURL=governance.js.map